/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback, useMemo } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import type { ShopperProducts, ShopperSearch } from '@/scapi';

/**
 * Both rows scroll horizontally and NEITHER live row paints a scrollbar for it.
 * Live's own markup is a bare `overflow-auto` on the track — the PLP's
 * `ul.overflow-auto.whitespace-nowrap` at
 * `.reference/captures/bg-plp-m-440/App.tsx:218`, the SRP's
 * `div.overflow-auto.whitespace-nowrap` at `bg-search-m-375/App.tsx:146` — and
 * neither the owner's `.reference/live-shots/bg-plp-390.png` nor
 * `bg-search-390.png` shows a bar over it. Ours does, on any machine whose OS is
 * set to show scrollbars always rather than as an overlay, which is where the
 * owner reported it on 2026-08-23 for the PLP row.
 *
 * OVERFLOW STAYS. The bar is suppressed, not the scrolling — removing
 * `overflow-x-auto` would make the tabs past the fold unreachable, which is a
 * strictly worse defect than a visible bar. Both rows are re-proved after this
 * lands: `scrollWidth > clientWidth`, and every tab still reachable by Tab.
 *
 * THE SRP ROW TAKES IT TOO, and that is one line of deliberate scope. It is the
 * same construction in the same file with the same exposure on the same brand's
 * mobile site, and live has no bar on either — leaving one half is the
 * half-applied shape this surface has already paid for once.
 *
 * The triple is the one already landed twice in this tree
 * (`home-content/quick-links.tsx`, `image-gallery/index.tsx`): the standard
 * property for Firefox, the legacy one for old Edge, and the WebKit
 * pseudo-element for Chrome and Safari.
 */
const HIDE_SCROLLBAR = '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';

/**
 * BG's SRP category tab row — the scrollable
 * `All Results (3806) · Women's Clothing (3291) · Men (315) · …` strip that sits
 * between the masthead rule and the grid.
 *
 * A BG BAND. Live NM's mobile SRP runs its heading straight into the sticky
 * Filter/Sort bar with nothing between them
 * (.reference/captures/search-m-390/App.tsx:212-216) — there is no NM arm to
 * token, which is why this is its own component behind `SearchUx.categoryTabs`.
 *
 * STRUCTURE, read in document order from
 * `.reference/captures/bg-search-m-375/App.tsx:146` and its siblings:
 *
 *   div.overflow-auto.whitespace-nowrap
 *     span.inline.text-left.mt-[16px].leading-[17.94px].pr-[35px]
 *       button.border-b.inline-block.text-center.bg-black/0
 *              .border-black.text-[14px].leading-[19.32px]
 *         "All Results  " span.text-center "(3742)"
 *     span…  button… (no `border-black`, `text-[rgb(121,_121,_121)]`)
 *         "Men " "(315)" · "Kids " "(196)" · "Handbags " "(1)" · "Home " "(1)"
 *
 * THE SELECTED STATE IS THE BORDER, NOT A FILL. Every tab carries `border-b`;
 * the active one adds `border-black` and drops the grey text colour, so the
 * inactive tabs draw a transparent underline of the same thickness and nothing
 * shifts when the selection moves. Reproduced with the same pair of tokens
 * rather than with a `border-b` that appears only when active.
 *
 * DATA. The tabs are the `cgid` refinement SCAPI already returns for the query
 * — the same values the facet rail lists — with the query's own total standing
 * in for the leading "All Results" tab. Selection round-trips through the same
 * `?refine=cgid=<id>` param `CategoryRefinements.toggleFilter` writes, so the
 * tab row and the rail can never disagree about what is selected, and a tab is
 * a real navigation rather than local state.
 *
 * WHAT IS NOT REPRODUCED, deliberately: live's row scrolls its selected tab
 * into view on load. Ours does not, because the scroll offset live restores is
 * not in any capture (a capture is one frozen frame) and guessing it would put
 * an unmeasured number on the page. The row is `overflow-x-auto`, so the tabs
 * past the fold are reachable exactly as live's are.
 */
export default function BgSearchCategoryTabs({
    refinements,
    total,
}: {
    refinements?: ShopperSearch.schemas['ProductSearchResult']['refinements'];
    total: number;
}): ReactElement | null {
    const { t } = useTranslation('search');
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();

    // Optimistic selection: while a tab's navigation is in flight, read the
    // TARGET's refine params so the underline moves on click rather than on
    // response. Same mechanism as CategoryRefinements' `effectiveRefines`.
    const effectiveSearch = navigation.location ? navigation.location.search : location.search;
    const selectedCgid = useMemo(() => {
        const refines = new URLSearchParams(effectiveSearch).getAll('refine');
        const pair = refines.find((refine) => refine.startsWith('cgid='));
        return pair ? pair.slice('cgid='.length) : null;
    }, [effectiveSearch]);

    const categories = useMemo(
        () => (refinements ?? []).find((refinement) => refinement.attributeId === 'cgid')?.values ?? [],
        [refinements]
    );

    const select = useCallback(
        (value: string | null) => {
            const params = new URLSearchParams(location.search);
            const kept = params.getAll('refine').filter((refine) => !refine.startsWith('cgid='));
            params.delete('refine');
            kept.forEach((refine) => params.append('refine', refine));
            if (value) params.append('refine', `cgid=${value}`);
            // Reset paging — both the legacy offset and the crawlable page
            // param — for the same reason the sort control does.
            params.set('offset', '0');
            params.delete('page');
            void navigate({ pathname: location.pathname, search: `?${params.toString()}` });
        },
        [location, navigate]
    );

    // No cgid facet for this query means live has no row to draw either — the
    // tabs ARE the facet. Rendering a lone "All Results" tab would be an
    // ours-only band.
    if (categories.length === 0) return null;

    return (
        <div
            data-testid="bg-search-category-tabs"
            role="navigation"
            aria-label={t('bgCategoryTabsLabel', { defaultValue: 'Filter results by category' })}
            className={cn(
                'overflow-x-auto whitespace-nowrap',
                HIDE_SCROLLBAR,
                'mx-(--bg-srp-tabs-inset-inline) mt-(--bg-srp-tabs-space-top) mb-(--bg-srp-tabs-space-bottom)'
            )}>
            <Tab
                label={t('bgAllResults', { defaultValue: 'All Results' })}
                count={total}
                active={selectedCgid === null}
                onSelect={() => select(null)}
            />
            {categories.map((category) => (
                <Tab
                    key={category.value}
                    label={category.label || category.value || ''}
                    count={category.hitCount}
                    active={selectedCgid === category.value}
                    onSelect={() => select(category.value ?? null)}
                />
            ))}
        </div>
    );
}

/**
 * BG's PLP category tab row — `All · ACTIVEWEAR · COATS & JACKETS · DRESSES · …`
 * between the masthead rule and the product grid.
 *
 * SAME ROW AS THE SRP'S, DIFFERENT CONTENT — which is why it is an export beside
 * `BgSearchCategoryTabs` sharing its `Tab`, and not a second file. The chrome is
 * identical in the two captures (scrollable `whitespace-nowrap` strip, per-tab
 * `border-b`, active adds the black border and drops the grey). Three things
 * genuinely differ, all read off `.reference/captures/bg-plp-m-440/App.tsx:217-265`
 * rather than assumed from the SRP:
 *
 *  1. THE TABS NAVIGATE, they do not refine. Each is an `<a>` to a real category
 *     URL (`/c/womens-clothing-clothing-dresses-cat80001`), where the SRP's apply
 *     a `cgid` refinement to the current query. So these are links, and the row
 *     is a `<nav><ul>` of `<li>` exactly as live's is.
 *  2. NO COUNTS. Live's PLP tab text is the bare silo name; the SRP's carries
 *     `(3742)`. Passing no count is what produces that.
 *  3. UPPERCASE, AND BY CSS. Live's first tab reads `All` in mixed case while
 *     every silo reads `DRESSES`, `PANTS & SHORTS` — the silo names come from the
 *     catalogue in Title Case and the row uppercases them, so the accessible name
 *     and the URL keep the catalogue's own casing.
 *
 * ACTIVE STATE. `All` is the active tab on a category's own page, because on
 * `/category/<id>` the row's own category IS "all" of what is listed. Selecting a
 * silo is a real navigation to that silo's PLP, which then draws its own row from
 * its own children. Live's server decides the same thing the same way — every
 * tab in the capture is a plain href and the active one is marked in the markup.
 *
 * A FOURTH THING DIFFERS, AND IT IS THE ROW'S OWN BOX. Until 2026-08-23 this row
 * rendered `--bg-srp-tabs-*` — the SRP row's outer-box set, measured on BG's
 * SEARCH masthead. The chrome those tokens describe really is shared, which is
 * why the `Tab` below is; the OUTER BOX is not, and two of the three values were
 * wrong here. Live's PLP row is one element, `bg-plp-m-440/App.tsx:217`, with
 * `mt-[3px] ml-[16px] pb-6` and NO `mr` — where the SRP set gave 32 above and 16
 * on both sides. Both errors were in the owner's 2026-08-23 report: the row
 * starting 16px inboard of the page's own inset, and the rule above it reading as
 * too far from the row. The row now takes `--bg-plp-tabs-*` from
 * tokens/brand-bg/plp.css, where live's declaration, the ink deltas at 390 and
 * the reason the right inset is negative are all recorded. `BgSearchCategoryTabs`
 * above keeps the SRP set unchanged; nothing about that surface moves.
 */
export function BgPlpCategoryTabs({
    category,
    categoryHref,
}: {
    category?: ShopperProducts.schemas['Category'];
    /** Href of the category's own page — live's leading `All` tab points here. */
    categoryHref: string;
}): ReactElement | null {
    const { t } = useTranslation('search');
    const subcategories = category?.categories ?? [];

    // No silos means live has no row to draw either — a lone `All` tab would be
    // an ours-only band. Same reasoning as the SRP row's empty-facet guard.
    if (subcategories.length === 0) return null;

    return (
        <nav
            data-testid="bg-plp-category-tabs"
            aria-label={t('bgCategoryTabsLabel', { defaultValue: 'Filter results by category' })}
            className={cn(
                'overflow-x-auto whitespace-nowrap',
                HIDE_SCROLLBAR,
                'ml-(--bg-plp-tabs-inset-left) mr-(--bg-plp-tabs-inset-right)',
                'mt-(--bg-plp-tabs-space-top) mb-(--bg-plp-tabs-space-bottom)'
            )}>
            <ul className="inline">
                <Tab label={t('bgAllTab', { defaultValue: 'All' })} active href={categoryHref} />
                {subcategories.map((subcategory) => (
                    <Tab
                        key={subcategory.id}
                        label={subcategory.name || subcategory.id}
                        active={false}
                        uppercase
                        href={`/category/${subcategory.id}`}
                    />
                ))}
            </ul>
        </nav>
    );
}

/**
 * One tab of either row.
 *
 * `href` renders live's PLP shape (an `<a>` inside an `<li>`, a real navigation);
 * `onSelect` renders live's SRP shape (a `<button>` that toggles a refinement).
 * Both carry the same underline chrome, so the selected state cannot drift
 * between the two surfaces.
 */
function Tab({
    label,
    count,
    active,
    uppercase,
    onSelect,
    href,
}: {
    label: string;
    count?: number;
    active: boolean;
    uppercase?: boolean;
    onSelect?: () => void;
    href?: string;
}): ReactElement {
    const className = cn(
        'inline-block cursor-pointer border-b-(length:--bg-srp-tab-underline-width) focus-ring-button',
        'text-(length:--bg-srp-tab-font-size) leading-(--bg-srp-tab-line-height)',
        // Live uppercases the silo names in CSS; the text nodes stay Title Case.
        uppercase && 'uppercase',
        active
            ? '[border-bottom-color:var(--bg-srp-tab-underline-color)] text-(--bg-srp-tab-active-color)'
            : 'border-b-transparent text-(--bg-srp-tab-color)'
    );

    const content = (
        <>
            {label}
            {/* Live emits a literal space between the label and the count —
                `>All Results  <span>(3742)</span>` in the capture. JSX
                strips whitespace between adjacent lines, so the gap is an
                explicit margin rather than a text node we cannot rely on. */}
            {count !== undefined && <span className="ml-(--bg-srp-tab-count-gap)">({count})</span>}
        </>
    );

    if (href) {
        return (
            <li className="inline-block pr-(--bg-srp-tab-gap)">
                <Link
                    to={href}
                    data-testid="bg-plp-category-tab"
                    data-active={active ? 'true' : 'false'}
                    aria-current={active ? 'page' : undefined}
                    className={className}>
                    {content}
                </Link>
            </li>
        );
    }

    return (
        <span className="inline-block pr-(--bg-srp-tab-gap)">
            <button
                type="button"
                data-testid="bg-search-category-tab"
                data-active={active ? 'true' : 'false'}
                aria-current={active ? 'true' : undefined}
                onClick={onSelect}
                className={className}>
                {content}
            </button>
        </span>
    );
}
